import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { Copy, Check, Users, Filter, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  confirmed:       'bg-emerald-100 text-emerald-800',
  pending_payment: 'bg-amber-100 text-amber-800',
  cancelled:       'bg-red-100 text-red-700',
  waitlist:        'bg-blue-100 text-blue-800',
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function EventPlayersPage() {
  const { token } = useAuth();
  const [rows, setRows]       = useState([]);
  const [meta, setMeta]       = useState({ events: [], games: [], statuses: [], genders: [] });
  const [loading, setLoading] = useState(false);
  const [copied, setCopied]   = useState(false);

  // Filters
  const [eventSearch, setEventSearch] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [gender, setGender]     = useState('all');
  const [minAge, setMinAge]     = useState('');
  const [maxAge, setMaxAge]     = useState('');
  const [status, setStatus]     = useState('all');
  const [game, setGame]         = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [nameSearch, setNameSearch] = useState('');

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedEvent !== 'all') params.set('event_name', selectedEvent);
      if (gender !== 'all')        params.set('gender', gender);
      if (minAge)                  params.set('min_age', minAge);
      if (maxAge)                  params.set('max_age', maxAge);
      if (status !== 'all')        params.set('status', status);
      if (game !== 'all')          params.set('game', game);
      if (fromDate)                params.set('from_date', fromDate);
      if (toDate)                  params.set('to_date', toDate);

      const res = await fetch(`${API}/api/events/admin/event-players?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setRows(data.players || []);
      setMeta(data.meta || { events: [], games: [], statuses: [], genders: [] });
    } catch (e) {
      toast.error('Failed to load event players');
    } finally {
      setLoading(false);
    }
  }, [token, selectedEvent, gender, minAge, maxAge, status, game, fromDate, toDate]);

  useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

  // Client-side name search filter
  const filtered = useMemo(() => {
    if (!nameSearch.trim()) return rows;
    const q = nameSearch.toLowerCase();
    return rows.filter(r =>
      (r.player_name || '').toLowerCase().includes(q) ||
      (r.mobile || '').includes(q) ||
      (r.email || '').toLowerCase().includes(q)
    );
  }, [rows, nameSearch]);

  // Filtered events for the searchable event dropdown
  const filteredEventOptions = useMemo(() =>
    meta.events.filter(e => e.toLowerCase().includes(eventSearch.toLowerCase())),
    [meta.events, eventSearch]
  );

  const handleCopyNumbers = () => {
    const seen = new Set();
    const nums = [];
    for (const r of filtered) {
      const wa = r.whatsapp_number;
      if (wa && !seen.has(wa)) {
        seen.add(wa);
        nums.push(wa);
      }
    }
    if (nums.length === 0) {
      toast.warning('No valid WhatsApp numbers to copy');
      return;
    }
    navigator.clipboard.writeText(nums.join(','))
      .then(() => {
        setCopied(true);
        toast.success(`Copied ${nums.length} unique WA numbers`);
        setTimeout(() => setCopied(false), 2500);
      })
      .catch(() => toast.error('Copy failed — check browser permissions'));
  };

  const clearFilters = () => {
    setSelectedEvent('all');
    setGender('all');
    setMinAge('');
    setMaxAge('');
    setStatus('all');
    setGame('all');
    setFromDate('');
    setToDate('');
    setNameSearch('');
    setEventSearch('');
  };

  const hasFilters = selectedEvent !== 'all' || gender !== 'all' || minAge || maxAge ||
                     status !== 'all' || game !== 'all' || fromDate || toDate || nameSearch;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-5" data-testid="event-players-page">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Event Players</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {loading ? 'Loading…' : `${filtered.length} player${filtered.length !== 1 ? 's' : ''} across all events`}
            </p>
          </div>
          <div className="flex gap-2">
            {hasFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters} data-testid="ep-clear-filters">
                <X className="w-4 h-4 mr-1" /> Clear Filters
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleCopyNumbers}
              disabled={filtered.length === 0}
              data-testid="ep-copy-wa-numbers"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
              Copy WA Numbers
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="ep-filters">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
            <Filter className="w-3.5 h-3.5" /> Filters
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">

            {/* Name/mobile search */}
            <div className="relative lg:col-span-1">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Name / mobile / email"
                value={nameSearch}
                onChange={e => setNameSearch(e.target.value)}
                className="pl-8 text-sm"
                data-testid="ep-filter-name"
              />
            </div>

            {/* Event name — searchable */}
            <div className="relative">
              <select
                value={selectedEvent}
                onChange={e => setSelectedEvent(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="ep-filter-event"
              >
                <option value="all">All Events</option>
                {meta.events.map(ev => (
                  <option key={ev} value={ev}>{ev}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <Select value={status} onValueChange={setStatus} data-testid="ep-filter-status">
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {meta.statuses.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Gender */}
            <Select value={gender} onValueChange={setGender} data-testid="ep-filter-gender">
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="All Genders" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Genders</SelectItem>
                {meta.genders.length > 0
                  ? meta.genders.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)
                  : ['male','female','other'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)
                }
              </SelectContent>
            </Select>

            {/* Category / Sport */}
            <Select value={game} onValueChange={setGame} data-testid="ep-filter-game">
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {meta.games.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Age range */}
            <div className="flex gap-1.5 items-center">
              <Input
                type="number" min="0" max="100"
                placeholder="Min age"
                value={minAge}
                onChange={e => setMinAge(e.target.value)}
                className="text-sm"
                data-testid="ep-filter-min-age"
              />
              <span className="text-slate-400 text-xs">–</span>
              <Input
                type="number" min="0" max="100"
                placeholder="Max age"
                value={maxAge}
                onChange={e => setMaxAge(e.target.value)}
                className="text-sm"
                data-testid="ep-filter-max-age"
              />
            </div>

            {/* Registration date range */}
            <Input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              className="text-sm"
              placeholder="Reg from"
              data-testid="ep-filter-from-date"
            />
            <Input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              className="text-sm"
              placeholder="Reg to"
              data-testid="ep-filter-to-date"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="ep-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b text-xs text-slate-500 uppercase tracking-wide">
                  <th className="px-3 py-2.5 text-left font-semibold">#</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Player Name</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Mobile</th>
                  <th className="px-3 py-2.5 text-left font-semibold">WA Number</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Gender</th>
                  <th className="px-3 py-2.5 text-left font-semibold">DOB / Age</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Event</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Category</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Status</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Email</th>
                  <th className="px-3 py-2.5 text-left font-semibold">WA Eligible</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Reg Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={12} className="text-center py-12 text-slate-400">Loading…</td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center py-12 text-slate-400">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      No players found
                    </td>
                  </tr>
                ) : filtered.map((r, i) => (
                  <tr key={`${r.registration_id}-${r.mobile}-${i}`}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid="ep-player-row">
                    <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">
                      {r.player_name || <span className="text-slate-400 italic">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-700">{r.mobile || '—'}</td>
                    <td className="px-3 py-2 font-mono text-blue-700 text-xs">
                      {r.whatsapp_number || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 capitalize text-slate-600">{r.gender || '—'}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                      {r.date_of_birth
                        ? <>{r.date_of_birth} <span className="text-xs text-slate-400">({r.age}y)</span></>
                        : r.age ? `${r.age}y` : '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700 max-w-[180px] truncate" title={r.event_name}>
                      {r.event_name || '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{r.categories || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-600'}`}>
                        {r.status || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs max-w-[160px] truncate" title={r.email}>
                      {r.email || '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs font-medium ${r.wa_marketing === 'Eligible' ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {r.wa_marketing}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">
                      {fmtDate(r.registration_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length > 0 && (
            <div className="px-4 py-2.5 bg-slate-50 border-t text-xs text-slate-500 flex justify-between items-center">
              <span>
                {filtered.length} rows &nbsp;·&nbsp;
                {[...new Set(filtered.map(r => r.whatsapp_number).filter(Boolean))].length} unique WA numbers
              </span>
              <Button
                size="sm" variant="outline"
                onClick={handleCopyNumbers}
                className="h-7 text-xs"
                data-testid="ep-copy-wa-footer"
              >
                {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                Copy WA Numbers
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
